import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useHistory, useLocation } from 'react-router-dom'

import { CompanyBankAccountsDto, IWallet, PaginatedResponse, ReadWalletsResponseDto } from '@sparling/financial-common'
import { AppStateContext, useRest } from '../../../components/Context'
import { ICompanyData } from '../CompanyProfileTabs/interfaces'
import { Loader } from '../../../components/Loader'
import { TableComponent, IDataItem } from '../../../components/Table'
import { bankAccountsColumns, walletsColumns } from './companyAccountsList'
import { MemoShowCompanyAccountModal } from './ShowCompanyAccountModal'
import { MemoShowCompanyWalletModal } from './ShowCompanyWalletModal'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    marginTop: theme.spacing(2),
    padding: theme.spacing(4),
    height: '100%',
  },
  titleWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    minWidth: 400,
  },
  table: {
    paddingTop: theme.spacing(2),
  },
  buttonWrap: {
    justifyContent: 'flex-end',
  },
}))

interface ICompanyDetails {
  data?: ICompanyData
}

export const CompanyAccounts: FC<ICompanyDetails> = ({ data }) => {
  const classes = useStyles()
  const { rxios } = useRest()
  const { getIsRefresh } = useContext(AppStateContext)
  const history = useHistory()
  const location = useLocation()

  const [loading, setLoading] = useState<boolean>(true)
  const [refresh, setRefresh] = useState<boolean | undefined>(false)
  const [isOpenBankAccountsModal, setIsOpenBankAccountsModal] = useState<boolean>(false)
  const [isOpenWalletsModal, setIsOpenWalletsModal] = useState<boolean>(false)
  const [companyBankAccountId, setCompanyBankAccountId] = useState<number | null>(null)
  const [companyWalletId, setCompanyWalletId] = useState<number | null>(null)
  const [companyBankAccounts, setCompanyBankAccounts] =
    useState<PaginatedResponse<IDataItem<{ [K in keyof CompanyBankAccountsDto]: CompanyBankAccountsDto[K] }>>>()
  const [companyWallets, setCompanyWallets] =
    useState<PaginatedResponse<IDataItem<{ [K in keyof ReadWalletsResponseDto]: ReadWalletsResponseDto[K] }>>>()

  const searchParams = new URLSearchParams(location.search)
  const bankAccountsPage = parseInt(searchParams.get('bankPage') || '1', 10)
  const walletsPage = parseInt(searchParams.get('walletPage') || '1', 10)

  useEffect(() => setRefresh(getIsRefresh()), [setRefresh, getIsRefresh])

  const formMethods = useForm({
    mode: 'onBlur',
  })

  const getCompanyBankAccountId = useCallback((bankAccountId) => {
    setCompanyBankAccountId(bankAccountId)
  }, [])

  const toggleBankAccountsModal = useCallback(() => {
    setIsOpenBankAccountsModal((prevState) => !prevState)
    setCompanyBankAccountId(null)
  }, [setIsOpenBankAccountsModal])

  const getCompanyWalletId = useCallback((walletId) => {
    setCompanyWalletId(walletId)
  }, [])

  const toggleWalletsModal = useCallback(() => {
    setIsOpenWalletsModal((prevState) => !prevState)
    setCompanyWalletId(null)
  }, [setIsOpenWalletsModal])

  const toggleModalShowBankAccount = useCallback(
    () => setIsOpenBankAccountsModal((prevState) => !prevState),
    [setIsOpenBankAccountsModal],
  )

  const loadBankAccountsData = useCallback(
    async (pageNumber: number = bankAccountsPage) => {
      setLoading(true)

      if (data?.id) {
        rxios.get(`/company/${data?.id}/accounts?page=${pageNumber}&count=10`).subscribe(
          (next) => {
            setCompanyBankAccounts(next as PaginatedResponse<IDataItem<CompanyBankAccountsDto>>)
            searchParams.set('bankPage', pageNumber.toString())
            history.replace(`${location.pathname}?${searchParams.toString()}`)
            setLoading(false)
          },
          (error) => {
            toast.error(error.response.data.message)
            setLoading(false)
          },
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.id, rxios, history, location.pathname],
  )

  const loadWalletsData = useCallback(
    async (pageNumber: number = walletsPage) => {
      setLoading(true)

      if (data?.id) {
        rxios.get(`/company/${data?.id}/wallets?page=${pageNumber}&count=10`).subscribe(
          (next) => {
            setCompanyWallets(next as PaginatedResponse<IDataItem<ReadWalletsResponseDto>>)
            searchParams.set('walletPage', pageNumber.toString())
            history.replace(`${location.pathname}?${searchParams.toString()}`)
            setLoading(false)
          },
          (error) => {
            toast.error(error.response.data.message)
            setLoading(false)
          },
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.id, rxios, history, location.pathname],
  )

  useEffect(() => {
    loadBankAccountsData(bankAccountsPage)
    loadWalletsData(walletsPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, data?.id])

  return (
    <Grid container display="flex" flexDirection="row">
      <Grid item xs={12} md={6} pr={1.5}>
        <Grid container display="flex">
          {!loading && companyBankAccounts ? (
            <FormProvider {...formMethods}>
              <Grid container className={classes.container}>
                <Grid container className={classes.titleWrap}>
                  <Typography variant={'h6'}>Bank Accounts</Typography>
                </Grid>
                <Grid container className={classes.table}>
                  <TableComponent
                    getElementId={getCompanyBankAccountId}
                    columns={bankAccountsColumns}
                    data={companyBankAccounts.items || []}
                    params={companyBankAccounts.meta}
                    loadData={(pageNumber: number) => loadBankAccountsData(pageNumber)}
                    isShowEditIcon={false}
                    onInfo={toggleModalShowBankAccount}
                    loading={loading}
                  />
                </Grid>
              </Grid>
            </FormProvider>
          ) : (
            <Loader />
          )}

          {data && (
            <MemoShowCompanyAccountModal
              isOpen={isOpenBankAccountsModal}
              modalClose={toggleBankAccountsModal}
              companyId={data.id}
              companyBankAccountData={companyBankAccounts?.items.find((item) => item.id === companyBankAccountId)}
            />
          )}
        </Grid>
      </Grid>
      <Grid item xs={12} md={6} pl={1}>
        <Grid container display="flex">
          {!loading && companyWallets ? (
            <FormProvider {...formMethods}>
              <Grid container className={classes.container}>
                <Grid container className={classes.titleWrap}>
                  <Typography variant={'h6'}>Wallets</Typography>
                </Grid>
                <Grid container className={classes.table}>
                  <TableComponent
                    getElementId={getCompanyWalletId}
                    columns={walletsColumns}
                    data={companyWallets?.items || []}
                    params={
                      companyWallets?.meta as {
                        currentPage: number
                        itemsPerPage: number
                        itemCount: number
                        totalItems: number
                        totalPages: number
                      }
                    }
                    loadData={(pageNumber: number) => loadWalletsData(pageNumber)}
                    isShowEditIcon={false}
                    onInfo={toggleWalletsModal}
                    loading={loading}
                  />
                </Grid>
              </Grid>
            </FormProvider>
          ) : (
            <Loader />
          )}

          {data && (
            <MemoShowCompanyWalletModal
              isOpen={isOpenWalletsModal}
              modalClose={toggleWalletsModal}
              companyId={data.id}
              companyWalletData={
                companyWallets?.items.find((item) => item.id === companyWalletId) as unknown as IWallet
              }
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}
